import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useUpdatePermissionScopeCache } from "~hooks/useUpdatePermissionScopeCache.ts";
import type { Season, SeasonResponse } from "~types/season.ts";

export const useCreateSeason = () => {
  const { sport, organizationId, competitionId } = useParams();
  const request = useApi();
  const queryClient = useQueryClient();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const updatePermissionScopeCache = useUpdatePermissionScopeCache();

  return useMutation<Season | undefined, Error, Partial<Season>>({
    mutationFn: async (body) => {
      const response = await request<SeasonResponse>(`${sport}/o/${organizationId}/competitions/${competitionId}/seasons`, {
        method: "POST",
        body,
      });

      return response?.data?.[0];
    },
    onSuccess: async (createdSeason) => {
      if (!createdSeason?.seasonId || !sport || !organizationId) {
        return;
      }

      updatePermissionScopeCache(sport, organizationId, (updatedScope) => {
        if (updatedScope.resources.seasons) {
          updatedScope.resources.seasons[createdSeason.seasonId] = createdSeason;
        } else {
          updatedScope.resources.seasons = {
            [createdSeason.seasonId]: createdSeason,
          };
        }

        const leagueIdToUpdate = updatedScope.resources?.competitions?.[createdSeason.competitionId]?.leagueId || null;
        const leagueToUpdate = updatedScope.scope.leagueTree.find(({ leagueId }) => leagueId === leagueIdToUpdate);
        if (leagueToUpdate) {
          const competitionToUpdate = leagueToUpdate.competitions.find(({ competitionId: id }) => id === createdSeason.competitionId);
          if (competitionToUpdate) {
            competitionToUpdate.seasonIds.push(createdSeason.seasonId);
          }
        }
      });

      await Promise.all([
        invalidateCache(),
        queryClient.invalidateQueries({ queryKey: ["seasons", sport, organizationId, createdSeason.seasonId] }),
      ]);
    },
  });
};
